<script setup lang="ts">
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue';
import { useFilterStore } from '@/stores/components/filter';
import type { FilterGroup } from '@/types/filter';
import { computed, defineEmits, defineProps, ref, watch } from 'vue';

const filterStore = useFilterStore();
const emit = defineEmits(['apply-filters', 'filter-menu-opened']);

const props = defineProps<{
    filterGroups: FilterGroup[];
}>();

const isMenuOpen = ref(false);
const showConfirmDialog = ref(false);

const countAppliedFilters = computed(() => {
    const count = Object.values(filterStore.selectedFilters).filter((f) => f !== '').length;
    if (filterStore.selectedFilters['organization']) return count - 1;
    return count;
});

function clearFilters() {
    filterStore.selectedFilters = {};
}

function setSelectedFilterValue(filterProperty: string, value: string | null) {
    if (value == null || value === '') {
        delete filterStore.selectedFilters[filterProperty];
        return;
    }

    filterStore.selectedFilters[filterProperty] = value ?? '';
}

function filtersChanged(newFilters: Record<string, string>, oldFilters: Record<string, string>) {
    if (Object.keys(oldFilters).length !== Object.keys(newFilters).length) return true;

    for (const key in newFilters) {
        if (newFilters[key] !== oldFilters[key]) return true;
    }

    return false;
}

watch(
    () => ({ ...filterStore.selectedFilters }), // cloning needed here to get the old value correctly
    (newFilters, oldFilters) => {
        if (!filtersChanged(newFilters, oldFilters)) return;

        emit('apply-filters', filterStore.selectedFilters.value);
    },
    { deep: true }
);

function filterMenuOpened() {
    emit('filter-menu-opened');
}

const filterSearch = ref('');

function getFilteredOptions(filterGroup: FilterGroup) {
    if (!filterSearch.value) return filterGroup.options;

    return filterGroup.options.filter((option) => option.title.toLowerCase().includes(filterSearch.value.toLowerCase()));
}

watch(isMenuOpen, (newValue) => {
    if (newValue) filterMenuOpened();
});
</script>

<template>
    <div class="d-flex align-center">
        <v-menu location="bottom" class="ml-2" v-model="isMenuOpen">
            <template v-slot:activator="{ props }">
                <v-icon icon="filter_alt" class="filter-icon rounded-lg hover-main" v-bind="props" />
                <div class="active-filters-count bg-main ml-0" v-if="countAppliedFilters > 0">
                    <span class="text-white text-sm">({{ countAppliedFilters }})</span>
                </div>
            </template>

            <v-list class="v-col-12 pa-0">
                <div class="d-flex w-100">
                    <v-list-item @click="showConfirmDialog = true" v-if="countAppliedFilters > 0" class="bg-alt text-white text-center"
                        ><span class="px-2">Filters wissen</span></v-list-item
                    >
                    <v-list-item @click="showConfirmDialog = false" :class="['bg-main text-white text-center', { 'w-100': countAppliedFilters == 0 }]"
                        ><span class="px-2">Menu sluiten</span></v-list-item
                    >
                </div>
                <v-list-item class="hover-main" v-for="filterGroup in props.filterGroups" :key="filterGroup.property">
                    <v-menu location="right" class="filter-menu">
                        <template v-slot:activator="{ props }">
                            <div v-bind="props" class="filter-dropdown">
                                <div>
                                    <v-badge
                                        :color="filterStore.selectedFilters[filterGroup.property] ? 'green' : 'grey-darker'"
                                        size="small"
                                        inline
                                        dot
                                        class="pr-1"
                                    />
                                    <span>{{ filterGroup.title }}</span>
                                </div>

                                <v-icon icon="chevron_right" class="ml-2" />
                            </div>
                        </template>

                        <v-list class="pa-0 ml-4" style="margin-top: -12px">
                            <v-text-field
                                v-model="filterSearch"
                                placeholder="Zoeken..."
                                prepend-inner-icon="search"
                                clearable
                                hide-details
                                density="compact"
                                @click.stop
                            />
                            <v-divider />
                            <v-radio-group
                                :model-value="filterStore.selectedFilters[filterGroup.property] || ''"
                                @update:modelValue="(val) => setSelectedFilterValue(filterGroup.property, val)"
                                hide-details
                            >
                                <v-radio value="" label="Toon alles" class="pa-3 py-1 hover-main">
                                    <template #label>
                                        <span>Toon alles</span>
                                    </template>
                                </v-radio>
                                <v-radio
                                    :value="option.value"
                                    v-for="option in getFilteredOptions(filterGroup)"
                                    :key="option.value"
                                    class="pa-3 py-1 hover-main"
                                >
                                    <template #label>
                                        <div class="d-flex align-center">
                                            <v-icon v-if="option.icon" :icon="option.icon" size="18" class="mr-2" />
                                            <span>{{ option.title }}</span>
                                        </div>
                                    </template>
                                </v-radio>
                            </v-radio-group>
                        </v-list>
                    </v-menu>
                </v-list-item>
            </v-list>
        </v-menu>
    </div>

    <ConfirmDialog @continue="clearFilters" v-model="showConfirmDialog" text="Weet je zeker dat je alle filters wil verwijderen?" />
</template>

<style scoped>
:deep(.v-list) {
    margin-left: 0 !important;
}

:deep(.v-selection-control) {
    padding: 0 !important;
}

:deep(.v-selection-control .v-label) {
    width: 100%;
    height: 100%;
    margin: 0;
    display: flex;
    padding: 3px 15px 3px 3px;
}
.filter-menu {
    :deep(.v-list-item__content) {
        margin: 0;
        padding: 0;
    }
}
.v-list-item {
    padding: 0 !important;
}
.filter-dropdown {
    display: flex;
    align-items: center;
    margin: auto;
    padding: 10px 15px;
    height: 100%;
    justify-content: space-between;
}
.filter-icon {
    font-size: 35px;
    color: var(--c-grey-darkest);
    margin-right: 8px;
    cursor: pointer;
}

.active-filters-count {
    justify-content: center;
    align-items: center;
    display: flex;
    width: 25px;
    height: 25px;
    position: relative;
    border-radius: 50%;
}

.v-radio .v-label span {
    font-size: 14px;
}
</style>
